import { Injectable } from '@angular/core';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
    EAddressActions,
    UpdateAddressSuccess,
    UpdateAddressFailure,
    GetAddressSuccess,
    GetAddress,
    GetAddressFailure,
    UpdateShippingAddress,
    SetPrincipalAddress,
    SetPrincipalAddressSuccess,
    SetPrincipalAddressFailure,
    DeleteShippingAddress,
    DeleteShippingAddressSuccess,
    DeleteShippingAddressFailure,
    CreateShippingAddress,
    CreateShippingAddressSuccess,
    CreateShippingAddressFailure
} from '../actions/address.actions';
import { AddressService } from '../../services/address.service';
import { ConstsHelper } from '../../../../helpers/consts.helper';
import { HttpErrorResponse } from '@angular/common/http';
import { AddressInterface } from '../../models/address.interface';
import { AbstractEffects } from '../../../shared/abstract.effects';
import { VisibilityConfirmationAddress } from '../actions/confirmation-address.actions';
import { SaveCustomerMission } from '../../../shared/store/actions/customer-mission.actions';
import { MfaInterface } from '../../../shared/models/mfa.interface';
import { UpdateMfaVisibility } from '../../../shared/store/actions/mfa.actions';

@Injectable()
export class AddressEffects extends AbstractEffects {

    address: AddressInterface = null;
    mfaAction: string = null;
    mfa: MfaInterface|null = null;
    mfaDestination: string = null;
    id: number = null;
    password?: string = null;
    isPrincipal: boolean = null;

    constructor(
        private router: Router,
        private actions: Actions,
        private addressService: AddressService,
        public override store: Store<any>
    ) {
        super(store);
    }

    getAddress = createEffect(() => this.actions.pipe(
        ofType<GetAddress>(EAddressActions.GetAddress), // Type of action, listen to the event dispatched
        switchMap(() => this.addressService.get()), // Call service to get customer
        switchMap((response: Array<AddressInterface>) => of(new GetAddressSuccess(response))), // Dispatch success event that will be catched up by reducer
        catchError((error: HttpErrorResponse) => {
            // Check invalid token
            if ([401, 403].indexOf(error.status) !== -1) {
                this.ResendRequestWithNewToken(new GetAddress());
            } else {
                this.DisplayMessage('error_with_retry', 400, new GetAddressFailure());
            }
            return throwError(error);
        })
    ));

    updateShippingAddress = createEffect(() => this.actions.pipe(
        ofType<UpdateShippingAddress>(EAddressActions.UpdateShippingAddress),
        tap(params => {
            this.address = params.payload;
            this.mfa = params.mfa;
        }),
        switchMap(() => this.addressService.update(this.address, this.mfa)),
        switchMap(() => of(new UpdateAddressSuccess())),
        tap ( () => {
            if (this.mfa) {
                this.mfa.visibility = false;
                this.store.dispatch(new UpdateMfaVisibility(this.mfa));
            }
            this.store.dispatch(new SaveCustomerMission(Object.assign({step: 1}, this.address), ConstsHelper.PROCEDURE_DELIVERY_ADDRESS_CHANGE, 'account'));
            this.store.dispatch(new VisibilityConfirmationAddress(false));
            this.router.navigate(['/compte/adresses']);
        }),
        catchError((error: HttpErrorResponse) => {
            if ([401, 403].indexOf(error.status) !== -1) {
                this.ResendRequestWithNewToken(new UpdateShippingAddress(this.address, this.mfa));
            } else if (this.mfa && error.error.error_message && error.error.error_message.includes('invalid_code')) {
                this.mfa.errorMessage = ConstsHelper.ERROR_MESSAGE_MFA_INVALID_CODE;
                this.store.dispatch(new UpdateMfaVisibility(this.mfa));
            } else if (this.mfa && error.error.error_message && error.error.error_message.includes('blocked')) {
                this.mfa.errorMessage = ConstsHelper.ERROR_MESSAGE_MFA_BLOCKED;
                this.store.dispatch(new UpdateMfaVisibility(this.mfa));
            } else {
                if ('Invalid address' === error.error.error_message) {
                    this.store.dispatch(new VisibilityConfirmationAddress(true));
                } else {
                    this.DisplayMessage('error_with_retry', 400, new UpdateAddressFailure());
                }
            }
            return throwError(error);
        })
    ));

    setPrincipalAddress = createEffect(() => this.actions.pipe(
        ofType<SetPrincipalAddress>(EAddressActions.SetPrincipalAddress),
        tap(params => {
            this.id = params.id;
            this.password = params.password;
            this.isPrincipal = params.isPrincipal;
        }),
        switchMap(() => this.addressService.setPrincipalAddress(this.id, this.isPrincipal, this.password)),
        tap(() => {
            this.store.dispatch(new GetAddress());
        }),
        switchMap(() => of(new SetPrincipalAddressSuccess())),
        catchError((error: HttpErrorResponse) => {
            if ([401, 403].indexOf(error.status) !== -1) {
                this.ResendRequestWithNewToken(new SetPrincipalAddress(this.id, this.isPrincipal, this.password));
            } else {
                this.DisplayMessage('error_with_retry', 400, new SetPrincipalAddressFailure());
            }
            return throwError(error);
        })
    ));

    deleteShippingAddress = createEffect(() => this.actions.pipe(
        ofType<DeleteShippingAddress>(EAddressActions.DeleteShippingAddress),
        tap(params => {
            this.id = params.id;
            this.password = params.password;
        }),
        switchMap(() => this.addressService.deleteShippingAddress(this.id, this.password)),
        switchMap(() => of(new DeleteShippingAddressSuccess())),
        catchError((error: HttpErrorResponse) => {
            if ([401, 403].indexOf(error.status) !== -1) {
                this.ResendRequestWithNewToken(new DeleteShippingAddress(this.id, this.password));
            } else {
                this.DisplayMessage('error_with_retry', 400, new DeleteShippingAddressFailure());
            }
            return throwError(error);
        })
    ));

    createShippingAddress = createEffect(() => this.actions.pipe(
        ofType<CreateShippingAddress>(EAddressActions.CreateShippingAddress),
        tap(params => {
            this.address = params.payload;
            this.mfa = params.mfa;
        }),
        switchMap(() => this.addressService.createShippingAddress(this.address, this.mfa)),
        switchMap(() => of(new CreateShippingAddressSuccess())),
        catchError((error: HttpErrorResponse) => {
            if ([401, 403].indexOf(error.status) !== -1) {
                this.ResendRequestWithNewToken(new CreateShippingAddress(this.address, this.mfa));
            } else if (error.error.error_message.includes('Invalid address')) {
                this.mfa.visibility = false;
                this.store.dispatch(new UpdateMfaVisibility(this.mfa));
                this.store.dispatch(new VisibilityConfirmationAddress(true));
            } else {
                this.DisplayMessage('error_with_retry', 400, new CreateShippingAddressFailure());
            }
            return throwError(error);
        })
    ));
}
